import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import { Services } from '@/types'
import { env, envUrlByService } from '@/env'
import { clearStoragePreservingAnnouncements, toast } from '@/helpers'

type ApiErrorData = {
  code: string
  message: string
}

export interface CustomAxiosConfig extends AxiosRequestConfig {
  ignoreErrorToast?: boolean
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    ignoreErrorToast?: boolean
  }
}

export class ApiError extends Error {
  code: string
  statusCode: number

  constructor(message: string, code: string, statusCode: number) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.statusCode = statusCode
  }
}

export type ErrorResponse = ApiError

let isRedirectingToLogin = false

function setupInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use((config) => {
    const accessToken = localStorage.getItem('access_token')
    if (accessToken) {
      config.headers.Authorization = accessToken
    }
    return config
  })

  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiErrorData>) => {
      if (error.response?.status === 401) {
        if (!isRedirectingToLogin) {
          isRedirectingToLogin = true
          clearStoragePreservingAnnouncements()
          sessionStorage.clear()
          window.location.href = '/session/signin'
        }

        return Promise.reject(
          new ApiError('Sessão expirada', 'session_expired', 401),
        )
      }

      if (error.response?.data) {
        const ignoreToast = error.config?.ignoreErrorToast
        if (!ignoreToast) {
          toast.error(error.response.data.message)
        }

        return Promise.reject(
          new ApiError(
            error.response.data.message,
            error.response.data.code,
            error.response.status,
          ),
        )
      }

      return Promise.reject(
        new ApiError(
          error.message || 'An unexpected error occurred',
          'unexpected_error',
          0,
        ),
      )
    },
  )

  return instance
}

const instances = new Map<Services, AxiosInstance>()

function getOrCreateInstance(service: Services): AxiosInstance {
  const existing = instances.get(service)
  if (existing) return existing

  const baseURL =
    envUrlByService[service][
      env.MODE as keyof (typeof envUrlByService)[typeof service]
    ]

  const instance = setupInterceptors(axios.create({ baseURL }))
  instances.set(service, instance)
  return instance
}

export function httpClient(service: Services) {
  return getOrCreateInstance(service)
}
